/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React examples
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PieChart from "examples/Charts/PieChart";

// Utilidades de exportación
import { exportDashboardToPDF, exportToExcel, handlePrint as printPage } from "utils/exportHelpers";

export default function AnalisisComparativo() {
  const [tipoComparacion, setTipoComparacion] = useState("empresas");
  const [metrica, setMetrica] = useState("ingresos");
  const [periodo, setPeriodo] = useState("mes");

  // Estados para datos de API
  const [comparacionEmpresas, setComparacionEmpresas] = useState({ labels: [], datasets: [] });
  const [comparacionSucursales, setComparacionSucursales] = useState({ labels: [], datasets: [] });
  const [comparacionPeriodos, setComparacionPeriodos] = useState({ labels: [], datasets: [] });
  const [participacionMercado, setParticipacionMercado] = useState({ labels: [], datasets: {} });
  const [comparacionRentabilidad, setComparacionRentabilidad] = useState({
    labels: [],
    datasets: [],
  });
  const [crecimientoInteranual, setCrecimientoInteranual] = useState({ labels: [], datasets: [] });
  const [tablaComparativa, setTablaComparativa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener datos del API
  useEffect(() => {
    const fetchDatos = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(API_ENDPOINTS.REPORTES.COMPARATIVO, {
          params: { tipo: tipoComparacion },
        });

        const data = response.data;
        const formatCurrency = (value) => `$${value.toLocaleString("es-MX")}`;

        // Configurar comparación de empresas
        if (tipoComparacion === "empresas" && data.comparacion_empresas) {
          const empresasData = data.comparacion_empresas;
          const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun"];

          setComparacionEmpresas({
            labels: meses,
            datasets: empresasData.map((empresa, index) => ({
              label: empresa.nombre,
              color: ["primary", "info", "success", "warning"][index % 4],
              data: empresa.ingresos_mensuales || [0, 0, 0, 0, 0, 0],
            })),
          });

          // Tabla comparativa de empresas
          setTablaComparativa(
            empresasData.map((empresa) => ({
              empresa: empresa.nombre,
              ingresos: formatCurrency(empresa.ingresos_totales || 0),
              costos: formatCurrency(empresa.costos_totales || 0),
              utilidad: formatCurrency(empresa.utilidad || 0),
              margen: `${(empresa.margen || 0).toFixed(1)}%`,
              crecimiento: `+${(empresa.crecimiento || 0).toFixed(1)}%`,
            }))
          );

          // Participación de mercado
          const totalIngresos = empresasData.reduce((sum, e) => sum + (e.ingresos_totales || 0), 0);
          setParticipacionMercado({
            labels: empresasData.map((e) => e.nombre),
            datasets: {
              label: "Participación %",
              backgroundColors: ["primary", "info", "success", "warning"],
              data: empresasData.map((e) =>
                (((e.ingresos_totales || 0) / totalIngresos) * 100).toFixed(1)
              ),
            },
          });

          // Rentabilidad
          setComparacionRentabilidad({
            labels: empresasData.map((e) => e.nombre),
            datasets: [
              {
                label: "Margen Neto %",
                color: "success",
                data: empresasData.map((e) => (e.margen || 0).toFixed(1)),
              },
            ],
          });

          // Crecimiento
          setCrecimientoInteranual({
            labels: empresasData.map((e) => e.nombre),
            datasets: [
              {
                label: "Crecimiento %",
                color: "info",
                data: empresasData.map((e) => (e.crecimiento || 0).toFixed(1)),
              },
            ],
          });
        }

        // Configurar comparación de sucursales
        if (tipoComparacion === "sucursales" && data.comparacion_sucursales) {
          const sucursalesData = data.comparacion_sucursales;

          setComparacionSucursales({
            labels: sucursalesData.map((s) => s.nombre),
            datasets: [
              {
                label: "Junio 2024",
                color: "success",
                data: sucursalesData.map((s) => s.ventas_mes_actual || 0),
              },
              {
                label: "Mayo 2024",
                color: "info",
                data: sucursalesData.map((s) => s.ventas_mes_anterior || 0),
              },
            ],
          });

          // Tabla comparativa de sucursales
          setTablaComparativa(
            sucursalesData.map((suc) => ({
              sucursal: suc.nombre,
              ventas: formatCurrency(suc.ventas_totales || 0),
              transacciones: (suc.num_transacciones || 0).toLocaleString(),
              ticketProm: formatCurrency(suc.ticket_promedio || 0),
              eficiencia: `${(suc.eficiencia || 0).toFixed(0)}%`,
            }))
          );
        }

        // Configurar comparación de períodos
        if (tipoComparacion === "periodos" && data.comparacion_periodos) {
          const periodosData = data.comparacion_periodos;

          setComparacionPeriodos({
            labels: ["Q1", "Q2", "Q3", "Q4"],
            datasets: Object.keys(periodosData).map((year, index) => ({
              label: year,
              color: ["primary", "warning", "secondary"][index % 3],
              data: periodosData[year] || [0, 0, 0, 0],
            })),
          });
        }
      } catch (error) {
        console.error("Error al cargar datos comparativos:", error);
        setError("Error al cargar los datos");
      } finally {
        setLoading(false);
      }
    };

    fetchDatos();
  }, [tipoComparacion, metrica, periodo]);

  const handleExportPDF = async () => {
    await exportDashboardToPDF(
      "analisis-comparativo-content",
      `analisis-comparativo-${tipoComparacion}-${new Date().getTime()}.pdf`
    );
  };

  const handleExportExcel = () => {
    const data = comparacionEmpresas.datasets[0].data.map((value, index) => ({
      Empresa: comparacionEmpresas.labels[index],
      Ingresos: value,
      "Crecimiento %": comparacionEmpresas.datasets[1]?.data[index] || 0,
    }));

    exportToExcel(
      data,
      "Análisis Comparativo",
      `analisis-comparativo-${tipoComparacion}-${new Date().getTime()}.xlsx`
    );
  };

  const handlePrintReport = () => {
    printPage();
  };

  const renderEmpresas = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Comparación de Ingresos por Empresa
            </MDTypography>
            <DefaultLineChart chart={comparacionEmpresas} height="350px" />
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Participación de Mercado
            </MDTypography>
            <PieChart chart={participacionMercado} height="350px" />
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Comparación de Rentabilidad
            </MDTypography>
            <VerticalBarChart chart={comparacionRentabilidad} height="300px" />
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Crecimiento Interanual
            </MDTypography>
            <HorizontalBarChart chart={crecimientoInteranual} height="300px" />
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Tabla Comparativa - Empresas
            </MDTypography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Empresa</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Ingresos</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Costos</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Utilidad</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Margen</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Crecimiento</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tablaComparativa.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.empresa}</TableCell>
                      <TableCell align="right">{row.ingresos}</TableCell>
                      <TableCell align="right">{row.costos}</TableCell>
                      <TableCell align="right">{row.utilidad}</TableCell>
                      <TableCell align="right">{row.margen}</TableCell>
                      <TableCell align="right">
                        <MDTypography variant="caption" color="success" fontWeight="bold">
                          {row.crecimiento}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  const renderSucursales = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Comparación de Ventas por Sucursal
            </MDTypography>
            <VerticalBarChart chart={comparacionSucursales} height="350px" />
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Tabla Comparativa - Sucursales
            </MDTypography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Sucursal</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Ventas</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Transacciones</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Ticket Promedio</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Eficiencia</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tablaComparativa.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.sucursal}</TableCell>
                      <TableCell align="right">{row.ventas}</TableCell>
                      <TableCell align="right">{row.transacciones}</TableCell>
                      <TableCell align="right">{row.ticketProm}</TableCell>
                      <TableCell align="right">{row.eficiencia}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  const renderPeriodos = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Comparación de Ingresos por Trimestre
            </MDTypography>
            <VerticalBarChart chart={comparacionPeriodos} height="350px" />
          </MDBox>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Tabla Comparativa - Períodos
            </MDTypography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Período</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Ingresos</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Gastos</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Utilidad</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Margen</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tablaComparativa.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.periodo}</TableCell>
                      <TableCell align="right">{row.ingresos}</TableCell>
                      <TableCell align="right">{row.gastos}</TableCell>
                      <TableCell align="right">{row.utilidad}</TableCell>
                      <TableCell align="right">{row.margen}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  const renderContent = () => {
    switch (tipoComparacion) {
      case "empresas":
        return renderEmpresas();
      case "sucursales":
        return renderSucursales();
      case "periodos":
        return renderPeriodos();
      default:
        return renderEmpresas();
    }
  };

  return (
    <MDBox id="analisis-comparativo-content">
      {/* Filtros y Controles */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Tipo de Comparación"
                value={tipoComparacion}
                onChange={(e) => setTipoComparacion(e.target.value)}
                fullWidth
              >
                <MenuItem value="empresas">Por Empresas</MenuItem>
                <MenuItem value="sucursales">Por Sucursales</MenuItem>
                <MenuItem value="periodos">Por Períodos</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Métrica"
                value={metrica}
                onChange={(e) => setMetrica(e.target.value)}
                fullWidth
              >
                <MenuItem value="ingresos">Ingresos</MenuItem>
                <MenuItem value="utilidad">Utilidad</MenuItem>
                <MenuItem value="margen">Margen</MenuItem>
                <MenuItem value="crecimiento">Crecimiento</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Período"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                fullWidth
              >
                <MenuItem value="mes">Mes Actual</MenuItem>
                <MenuItem value="trimestre">Trimestre Actual</MenuItem>
                <MenuItem value="anio">Año Actual</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="dark" size="small" onClick={handleExportPDF}>
                  <Icon>picture_as_pdf</Icon>&nbsp;PDF
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={handleExportExcel}
                >
                  <Icon>table_chart</Icon>&nbsp;Excel
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* Contenido */}
      {renderContent()}

      {/* Conclusiones */}
      <Card sx={{ mt: 3 }}>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            Conclusiones del Análisis Comparativo
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MDTypography variant="button" fontWeight="bold" color="success">
                Puntos Destacados:
              </MDTypography>
              <MDTypography variant="caption" display="block" mt={1}>
                • Empresa Principal lidera con 49.1% de participación
              </MDTypography>
              <MDTypography variant="caption" display="block">
                • Crecimiento promedio del 15.3% en todas las empresas
              </MDTypography>
              <MDTypography variant="caption" display="block">
                • Márgenes netos superiores al 22% en todas las unidades
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDTypography variant="button" fontWeight="bold" color="warning">
                Oportunidades de Mejora:
              </MDTypography>
              <MDTypography variant="caption" display="block" mt={1}>
                • Distribuidora Norte tiene potencial de crecimiento
              </MDTypography>
              <MDTypography variant="caption" display="block">
                • Sucursales Oeste y Este requieren optimización
              </MDTypography>
              <MDTypography variant="caption" display="block">
                • Estandarizar procesos para mejorar eficiencia global
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
    </MDBox>
  );
}
